import { useEffect, useRef, useState, useCallback } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fdateShort } from "@/lib/format";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  ArrowLeft, Type, ImagePlus, QrCode, Shapes, Sparkles, Trash2, Copy, Download,
  ChevronUp, ChevronDown, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Plus, Save,
  Undo2, Redo2, ArrowUp, ArrowDown, ArrowRight,
} from "lucide-react";
import StationeryCanvas from "@/components/stationery/StationeryCanvas";
import DecorSvg, { DECOR_MOTIFS } from "@/components/stationery/DecorSvg";
import {
  TEMPLATES, TYPES, FONTS, instantiate, newTextLayer, newDecorLayer, newShapeLayer, newQrLayer, newImageLayer,
} from "@/lib/stationery";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StationeryEditor() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const w = wedding?.wedding;
  const [designs, setDesigns] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/weddings/${wid}/invitations`);
    setDesigns(data.filter((d) => !["menu", "ceremony"].includes(d.type) && d.canvas));
  }, [wid]);
  useEffect(() => { if (wid) load(); }, [wid, load]);

  const ctx = () => ({
    names: w?.names, dateFr: fdateShort(w?.wedding_date), city: w?.city,
    siteUrl: `${window.location.origin}/w/${w?.slug}`,
  });

  const createFrom = async (tpl) => {
    const canvas = instantiate(tpl, ctx());
    const { data } = await api.post(`/weddings/${wid}/invitations`, {
      type: "invitation", template: tpl.id, title: tpl.name, canvas, config: {},
    });
    setEditing(data);
    load();
  };

  const openDesign = (d) => setEditing(d);
  const del = async (d, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Supprimer « ${d.title} » ?`)) return;
    await api.delete(`/weddings/${wid}/invitations/${d.id}`);
    toast.success("Création supprimée");
    load();
  };

  if (!designs) return <Loader />;
  if (editing) return <Editor wid={wid} design={editing} onBack={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <PageHeader eyebrow="Créer · Papeterie" title="Faire-part"
        description="Choisissez un modèle, personnalisez chaque élément et exportez en PDF ou PNG." />

      {designs.length > 0 && (
        <div className="mb-10">
          <div className="eyebrow mb-4">Vos créations</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {designs.map((d) => (
              <div key={d.id} className="group cursor-pointer" onClick={() => openDesign(d)} data-testid={`design-${d.id}`}>
                <div className="rounded-lg overflow-hidden border bg-white flex items-center justify-center p-2 relative">
                  <StationeryCanvas canvas={d.canvas} scale={168 / 480} />
                  <button onClick={(e) => del(d, e)} data-testid={`del-design-${d.id}`}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="mt-2 text-sm font-medium truncate">{d.title}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="eyebrow mb-4">Modèles</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {TEMPLATES.map((t) => (
          <button key={t.id} onClick={() => createFrom(t)} data-testid={`template-${t.id}`}
            className="group text-left">
            <div className="rounded-lg overflow-hidden border bg-white flex items-center justify-center p-2 group-hover:shadow-xl transition-shadow">
              <StationeryCanvas canvas={instantiate(t, ctx())} scale={190 / 480} />
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div><div className="text-sm font-medium">{t.name}</div>
                <div className="text-xs text-stone-400">{t.mood}</div></div>
              <Plus className="w-4 h-4 text-stone-400 group-hover:text-champagne" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Editor({ wid, design, onBack }) {
  const [canvas, setCanvas] = useState(design.canvas);
  const [title, setTitle] = useState(design.title);
  const [selId, setSelId] = useState(null);
  const [scale, setScale] = useState(0.82);
  const [busy, setBusy] = useState(false);
  const stageRef = useRef(null);
  const fileRef = useRef(null);
  const saveTimer = useRef(null);
  const past = useRef([]);
  const future = useRef([]);
  const lastPush = useRef(0);
  const [, setHistTick] = useState(0);

  const sel = canvas.layers.find((l) => l.id === selId);

  useEffect(() => {
    const fit = () => setScale(Math.min(0.9, Math.max(0.42, (Math.min(window.innerWidth - 660, 520)) / 480)));
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, []);

  const persist = useCallback((c, t) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.patch(`/weddings/${wid}/invitations/${design.id}`, { canvas: c, title: t }).catch(() => {});
    }, 700);
  }, [wid, design.id]);

  // History: coalesce rapid successive changes (e.g. a continuous drag) into ONE undo step.
  const commit = (c, t = title) => {
    const now = Date.now();
    if (now - lastPush.current > 400) {
      past.current.push(canvas);
      if (past.current.length > 60) past.current.shift();
      future.current = [];
      setHistTick((x) => x + 1);
    }
    lastPush.current = now;
    setCanvas(c); persist(c, t);
  };
  const undo = () => {
    if (!past.current.length) return;
    future.current.push(canvas);
    const prev = past.current.pop();
    lastPush.current = 0;
    setCanvas(prev); persist(prev, title); setSelId(null); setHistTick((x) => x + 1);
  };
  const redo = () => {
    if (!future.current.length) return;
    past.current.push(canvas);
    const nxt = future.current.pop();
    lastPush.current = 0;
    setCanvas(nxt); persist(nxt, title); setSelId(null); setHistTick((x) => x + 1);
  };
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if ((e.ctrlKey || e.metaKey) && k === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const nudge = (dx, dy) => { if (sel) updLayer(sel.id, { x: (sel.x || 0) + dx, y: (sel.y || 0) + dy }); };
  const updLayer = (id, patch) => commit({ ...canvas, layers: canvas.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)) });
  const addLayer = (l) => { commit({ ...canvas, layers: [...canvas.layers, l] }); setSelId(l.id); };
  const removeLayer = (id) => { commit({ ...canvas, layers: canvas.layers.filter((l) => l.id !== id) }); setSelId(null); };
  const dupLayer = (id) => {
    const l = canvas.layers.find((x) => x.id === id); if (!l) return;
    const c = { ...l, id: "id" + Math.random().toString(36).slice(2, 10), x: l.x + 16, y: l.y + 16 };
    addLayer(c);
  };
  const moveLayer = (id, dir) => {
    const i = canvas.layers.findIndex((l) => l.id === id); if (i < 0) return;
    const j = dir === "up" ? i + 1 : i - 1; if (j < 0 || j >= canvas.layers.length) return;
    const arr = [...canvas.layers];[arr[i], arr[j]] = [arr[j], arr[i]];
    commit({ ...canvas, layers: arr });
  };

  const uploadPhoto = async (e, targetId) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/weddings/${wid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND}${data.url}`;
      if (targetId) updLayer(targetId, { src: url });
      else addLayer(newImageLayer(url));
    } catch { toast.error("Échec du téléversement"); } finally { setBusy(false); e.target.value = ""; }
  };

  const saveNow = async () => {
    clearTimeout(saveTimer.current);
    await api.patch(`/weddings/${wid}/invitations/${design.id}`, { canvas, title });
    toast.success("Enregistré");
  };

  const capture = async () => {
    const node = stageRef.current; const prev = node.style.transform;
    setSelId(null); await new Promise((r) => setTimeout(r, 30));
    node.style.transform = "none";
    const c = await html2canvas(node, { scale: 2, backgroundColor: null, useCORS: true, logging: false });
    node.style.transform = prev;
    return c;
  };
  const exportPNG = async () => {
    setBusy(true);
    try { const c = await capture(); const a = document.createElement("a"); a.download = `${title || "faire-part"}.png`; a.href = c.toDataURL("image/png"); a.click(); toast.success("PNG exporté"); }
    catch { toast.error("Échec de l'export"); } finally { setBusy(false); }
  };
  const exportPDF = async () => {
    setBusy(true);
    try {
      const c = await capture(); const img = c.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "mm", format: [105, 148], orientation: "portrait" });
      pdf.addImage(img, "PNG", 0, 0, 105, 148); pdf.save(`${title || "faire-part"}.pdf`); toast.success("PDF exporté");
    } catch { toast.error("Échec de l'export"); } finally { setBusy(false); }
  };

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e, fileRef.current?.dataset.target)} />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="editor-back"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
        <Input value={title} onChange={(e) => { setTitle(e.target.value); persist(canvas, e.target.value); }}
          className="h-9 w-48" data-testid="design-title" />
        <div className="flex-1" />
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={undo} disabled={!past.current.length} title="Annuler (Ctrl+Z)" data-testid="undo-btn"><Undo2 className="w-4 h-4" /></Button>
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={redo} disabled={!future.current.length} title="Rétablir (Ctrl+Maj+Z)" data-testid="redo-btn"><Redo2 className="w-4 h-4" /></Button>
        <Button variant="outline" size="sm" onClick={saveNow} data-testid="save-design"><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
        <Button variant="outline" size="sm" onClick={exportPNG} disabled={busy} data-testid="export-png"><Download className="w-4 h-4 mr-1" /> PNG</Button>
        <Button size="sm" onClick={exportPDF} disabled={busy} className="rounded-full" data-testid="export-pdf"><Download className="w-4 h-4 mr-1" /> PDF</Button>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr_280px] gap-5 items-start">
        {/* Tools */}
        <div className="card-lux p-4 space-y-4">
          <div>
            <div className="eyebrow mb-2">Ajouter</div>
            <div className="grid grid-cols-2 gap-2">
              <ToolBtn icon={Type} label="Texte" testid="add-text" onClick={() => addLayer(newTextLayer())} />
              <ToolBtn icon={ImagePlus} label="Photo" testid="add-photo" onClick={() => { fileRef.current.dataset.target = ""; fileRef.current.click(); }} />
              <ToolBtn icon={QrCode} label="QR code" testid="add-qr" onClick={() => addLayer(newQrLayer(`${window.location.origin}`))} />
              <ToolBtn icon={Shapes} label="Ligne" testid="add-line" onClick={() => addLayer(newShapeLayer("line"))} />
              <ToolBtn icon={Shapes} label="Rectangle" testid="add-rect" onClick={() => addLayer(newShapeLayer("rect"))} />
              <ToolBtn icon={Shapes} label="Cercle" testid="add-circle" onClick={() => addLayer(newShapeLayer("circle"))} />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Ornements</div>
            <div className="grid grid-cols-3 gap-1.5">
              {DECOR_MOTIFS.map((m) => (
                <button key={m} onClick={() => addLayer(newDecorLayer(m))} data-testid={`add-decor-${m}`}
                  className="aspect-square rounded-md border p-1.5 hover:bg-accent" title={m}>
                  <DecorMini motif={m} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Fond</Label>
            <input type="color" value={/^#/.test(canvas.bg) ? canvas.bg : "#ffffff"} data-testid="canvas-bg"
              onChange={(e) => commit({ ...canvas, bg: e.target.value })} className="mt-1 w-full h-9 rounded cursor-pointer border" />
          </div>
        </div>

        {/* Canvas */}
        <div className="flex flex-col items-center py-2">
          <StationeryCanvas canvas={canvas} scale={scale} interactive selectedId={selId}
            onSelect={setSelId} onChange={updLayer} stageRef={stageRef} />
          {sel && (
            <div className="flex items-center gap-1.5 mt-3" data-testid="nudge-controls">
              <span className="text-xs text-stone-400 mr-1">Ajuster :</span>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nudge(-2, 0)} data-testid="nudge-left"><ArrowLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nudge(0, -2)} data-testid="nudge-up"><ArrowUp className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nudge(0, 2)} data-testid="nudge-down"><ArrowDown className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nudge(2, 0)} data-testid="nudge-right"><ArrowRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>

        {/* Properties */}
        <div className="card-lux p-4 space-y-3 min-h-[200px]" data-testid="props-panel">
          {!sel ? (
            <div className="text-sm text-stone-400 text-center py-8">Sélectionnez un élément sur le faire-part pour le modifier.</div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="eyebrow">{labelFor(sel)}</div>
                <div className="flex gap-1">
                  <IcoBtn icon={ChevronUp} onClick={() => moveLayer(sel.id, "up")} title="Avancer" />
                  <IcoBtn icon={ChevronDown} onClick={() => moveLayer(sel.id, "down")} title="Reculer" />
                  <IcoBtn icon={Copy} onClick={() => dupLayer(sel.id)} title="Dupliquer" testid="dup-layer" />
                  <IcoBtn icon={Trash2} onClick={() => removeLayer(sel.id)} title="Supprimer" testid="del-layer" danger />
                </div>
              </div>
              {sel.type === "text" && <TextProps l={sel} upd={(p) => updLayer(sel.id, p)} />}
              {sel.type === "image" && <ImageProps l={sel} upd={(p) => updLayer(sel.id, p)} onReplace={() => { fileRef.current.dataset.target = sel.id; fileRef.current.click(); }} />}
              {sel.type === "decor" && <DecorProps l={sel} upd={(p) => updLayer(sel.id, p)} />}
              {sel.type === "shape" && <ShapeProps l={sel} upd={(p) => updLayer(sel.id, p)} />}
              {sel.type === "qr" && <QrProps l={sel} upd={(p) => updLayer(sel.id, p)} />}
              <Rotation l={sel} upd={(p) => updLayer(sel.id, p)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const labelFor = (l) => ({ text: "Texte", image: "Photo", decor: "Ornement", shape: "Forme", qr: "QR code" }[l.type] || "Élément");

function ToolBtn({ icon: Icon, label, onClick, testid }) {
  return <button onClick={onClick} data-testid={testid}
    className="flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs hover:bg-accent transition">
    <Icon className="w-4 h-4" /> {label}</button>;
}
function IcoBtn({ icon: Icon, onClick, title, danger, testid }) {
  return <button onClick={onClick} title={title} data-testid={testid}
    className={`w-7 h-7 rounded-md border flex items-center justify-center hover:bg-accent ${danger ? "hover:text-destructive" : ""}`}>
    <Icon className="w-3.5 h-3.5" /></button>;
}
function DecorMini({ motif }) {
  return <DecorSvg motif={motif} color="#8A7A5C" />;
}
function ColorRow({ label, value, onChange, testid }) {
  return <div className="flex items-center justify-between"><Label className="text-xs">{label}</Label>
    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-10 h-7 rounded cursor-pointer border" /></div>;
}

function TextProps({ l, upd }) {
  return (
    <div className="space-y-3">
      <Textarea value={l.text} onChange={(e) => upd({ text: e.target.value })} rows={2} className="text-sm" data-testid="text-content" />
      <div><Label className="text-xs">Police</Label>
        <select value={l.font} onChange={(e) => upd({ font: e.target.value })} data-testid="text-font"
          className="mt-1 w-full h-9 rounded-md border px-2 text-sm bg-white">
          {FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}</select></div>
      <div><Label className="text-xs">Taille · {l.size}px</Label>
        <Slider value={[l.size]} min={8} max={80} step={1} onValueChange={([v]) => upd({ size: v })} className="mt-2" /></div>
      <ColorRow label="Couleur" value={l.color} onChange={(c) => upd({ color: c })} testid="text-color" />
      <div className="flex gap-1">
        <Toggle active={l.weight >= 600} onClick={() => upd({ weight: l.weight >= 600 ? 400 : 700 })} icon={Bold} testid="text-bold" />
        <Toggle active={l.italic} onClick={() => upd({ italic: !l.italic })} icon={Italic} testid="text-italic" />
        <div className="w-px bg-border mx-1" />
        <Toggle active={l.align === "left"} onClick={() => upd({ align: "left" })} icon={AlignLeft} />
        <Toggle active={l.align === "center"} onClick={() => upd({ align: "center" })} icon={AlignCenter} />
        <Toggle active={l.align === "right"} onClick={() => upd({ align: "right" })} icon={AlignRight} />
      </div>
      <div><Label className="text-xs">Interlettrage · {l.spacing}px</Label>
        <Slider value={[l.spacing]} min={0} max={16} step={0.5} onValueChange={([v]) => upd({ spacing: v })} className="mt-2" /></div>
    </div>
  );
}
function ImageProps({ l, upd, onReplace }) {
  return (
    <div className="space-y-3">
      <Button variant="outline" size="sm" className="w-full" onClick={onReplace} data-testid="replace-photo"><ImagePlus className="w-4 h-4 mr-1" /> Remplacer la photo</Button>
      <div><Label className="text-xs">Cadrage</Label>
        <select value={l.fit} onChange={(e) => upd({ fit: e.target.value })} className="mt-1 w-full h-9 rounded-md border px-2 text-sm bg-white">
          <option value="cover">Remplir (cover)</option><option value="contain">Entier (contain)</option></select></div>
      <div><Label className="text-xs">Arrondi · {l.radius}px</Label>
        <Slider value={[l.radius]} min={0} max={120} step={1} onValueChange={([v]) => upd({ radius: v })} className="mt-2" /></div>
    </div>
  );
}
function DecorProps({ l, upd }) {
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Motif</Label>
        <select value={l.motif} onChange={(e) => upd({ motif: e.target.value })} data-testid="decor-motif"
          className="mt-1 w-full h-9 rounded-md border px-2 text-sm bg-white">
          {DECOR_MOTIFS.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
      <ColorRow label="Couleur" value={l.color} onChange={(c) => upd({ color: c })} testid="decor-color" />
    </div>
  );
}
function ShapeProps({ l, upd }) {
  return (
    <div className="space-y-3">
      <ColorRow label="Couleur" value={l.fill} onChange={(c) => upd({ fill: c, stroke: c })} testid="shape-color" />
      {l.shape !== "line" && (
        <div><Label className="text-xs">Bordure · {l.strokeWidth}px</Label>
          <Slider value={[l.strokeWidth]} min={0} max={10} step={1} onValueChange={([v]) => upd({ strokeWidth: v })} className="mt-2" /></div>
      )}
    </div>
  );
}
function QrProps({ l, upd }) {
  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Lien / texte du QR</Label>
        <Input value={l.value} onChange={(e) => upd({ value: e.target.value })} className="mt-1 h-9 text-xs" data-testid="qr-value" /></div>
      <ColorRow label="Couleur" value={l.color} onChange={(c) => upd({ color: c })} />
    </div>
  );
}
function Rotation({ l, upd }) {
  return <div><Label className="text-xs">Rotation · {l.rotation || 0}°</Label>
    <Slider value={[l.rotation || 0]} min={-180} max={180} step={1} onValueChange={([v]) => upd({ rotation: v })} className="mt-2" /></div>;
}
function Toggle({ active, onClick, icon: Icon, testid }) {
  return <button onClick={onClick} data-testid={testid}
    className={`w-8 h-8 rounded-md border flex items-center justify-center ${active ? "bg-stone-900 text-white" : "bg-white"}`}>
    <Icon className="w-3.5 h-3.5" /></button>;
}
